import type { B2BEventType, B2BPublicEventCategory } from './requestTypes';

// AUTH

export interface AuthSignupSuccessResponse {
  session: string;
  message: string;
  requiresCaptcha?: true;
}

export type AuthSignupResponse = AuthSignupSuccessResponse;

export const extractSignupSession = (data: unknown): string | undefined => {
  if (data === null || typeof data !== 'object') {
    return undefined;
  }
  const o = data as Record<string, unknown>;
  return typeof o.session === 'string' && o.session.length > 0 ? o.session : undefined;
};

export const parseSignupCaptchaRequirement = (data: unknown): { required: boolean } => {
  if (data === null || typeof data !== 'object') {
    return { required: false };
  }
  const o = data as Record<string, unknown>;
  return { required: o.requiresCaptcha === true };
};

export interface AuthOtpResponse {
  accessToken: string;
  refreshToken: string;
  type: string;
  expiresIn: number;
  isCompleted: boolean;
}

export interface SignInProps {
  token: string;
  expiresIn: number;
  tokenType: string;
  refreshToken: string;
  refreshTokenExpireIn: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  type: string;
  expiresIn: number;
  isCompleted: boolean;
  refreshToken?: string;
}

// HOMEPAGE

export interface TeamInfo {
  id: number;
  name: string;
  logo: string;
}

export enum MatchState {
  PREVIEW = 'PREVIEW',
  ON_SALE = 'ON_SALE',
  SOLD_OUT = 'SOLD_OUT',
}

export interface MatchesResponse {
  items: MatchResponse[];
}

export interface ConcertsResponse {
  items: ConcertResponse[];
}

export interface FeaturedEventItem {
  id: number;
  featuredEventImage?: string | null;
  match?: MatchResponse | null;
  concert?: ConcertResponse | null;
}

export interface FeaturedEventsResponse {
  items: FeaturedEventItem[];
}

// EVENT

export interface PricingTicketType {
  ticketType: string;
  price: number;
  label: string;
  description: string;
  mandatory: boolean;
}

export interface Pricing {
  label: string;
  category: string;
  price?: number;
  arDescription?: string;
  kuDescription?: string;
  ticketTypes?: PricingTicketType[];
}

export interface GoodToKnowItem {
  title: string;
  description: string;
}

export interface TimeslotEntry {
  id: number;
  eventKey: string;
  dateAndTime: string | null;
  time: string | null;
  slotDescription: string | null;
  state: 'PREVIEW' | 'ON_SALE' | 'SOLD_OUT' | 'CANCELLED';
  soldOut: boolean;
  pricing?: Pricing[];
}

export interface TimeslotGroup {
  fullDate: string | null;
  date: string | null;
  slots: TimeslotEntry[];
}

export interface MatchResponse {
  teamA: TeamInfo;
  teamB: TeamInfo;
  id: number;
  city: string;
  title: string;
  subtitle: string | null;
  date: string | null;
  time: string;
  stadium?: string | null;
  startingPrice: number;
  currency: string;
  eventKey: string;
  eventId: number;
  state: MatchState;
  arSubtitle?: string | null;
  kuTitle?: string | null;
  kuSubtitle?: string | null;
  arAddress?: string | null;
  kuAddress?: string | null;
  eventGroupId?: number | null;
  location?: string | null;
  arDescription?: string | null;
  kuDescription?: string | null;
  pricing: Pricing[];
  maxAllowedTickets: number;
  requireAuthentication?: boolean;
  termAndConditions: string[];
  goodToKnow?: GoodToKnowItem[];
  isFreeEvent?: boolean;
  isInvitationBased?: boolean;
  hasTimeslots: boolean;
  parentEventId: number | null;
  timeslots?: TimeslotGroup[];
}

export interface ConcertResponse {
  imageLink: string;
  id: number;
  city: string;
  title: string;
  subtitle: string;
  date: string | null;
  time: string;
  stadium?: string | null;
  startingPrice: number;
  currency: string;
  eventId: number;
  eventKey: string;
  state: MatchState;
  arSubtitle?: string | null;
  kuTitle?: string | null;
  kuSubtitle?: string | null;
  arAddress?: string | null;
  kuAddress?: string | null;
  eventGroupId?: number | null;
  location?: string | null;
  arDescription?: string | null;
  kuDescription?: string | null;
  pricing: Pricing[];
  maxAllowedTickets: number;
  requireAuthentication: boolean;
  termAndConditions: string[];
  goodToKnow?: GoodToKnowItem[];
  isFreeEvent?: boolean;
  isInvitationBased?: boolean;
  hasTimeslots: boolean;
  parentEventId: number | null;
  timeslots?: TimeslotGroup[];
}

export interface CheckoutResponse {
  message: string;
  createdId: number;
  orderNumber: string;
  paymentLink: string | null;
}

export interface TicketPromoInfo {
  applied: boolean;
  code: string;
  message: string;
}

export interface TicketSummary {
  categoryKey: string;
  eligibleForPromoCode: boolean;
  price: number;
  appliedPromoCodesAmount: number;
  finalPrice: number;
  promoCode: TicketPromoInfo;
}

export enum PromoCodeStatus {
  PARTIALLY_VALID = 'PARTIALLY_VALID',
  FULLY_VALID = 'FULLY_VALID',
  NOT_VALID = 'NOT_VALID',
}

export interface CalculatePriceResponse {
  subTotal: number;
  appliedPromoCodesAmount: number;
  total: number;
  currency: string;
  promoCodeStatus: PromoCodeStatus;
  ticketsSummary: TicketSummary[];
}

export enum QrCodeStatus {
  NotYetAvailable = 'will be available 6 hours before the match',
  Available = 'available',
  NotAvailable = 'not available',
}

export interface TicketResponse {
  id: string;
  sector: string;
  section: string;
  row: string;
  seatNumber: string;
  gate: string;
  used: boolean;
  event: Event;
  holderName: string;
  ticketType?: string;
  ticketLabel?: string;
  holderPhone: string;
  qrCodeStatus: QrCodeStatus;
  qrCode: string;
  concert: ConcertResponse;
  match: MatchResponse;
  category: {
    key: string;
    label: string;
    color: string;
  };
  ticketOrderId: number;
  isForOffline?: boolean;
  invitationId?: string;
  slotDescription: string | null;
  parentEventId: number | null;
}

export interface OrderTicket {
  id: number;
  sector: string;
  price: number;
  holderName: string;
  holderPhone: string;
  ticketType?: string;
  ticketLabel?: string;
}

export interface OrderSummaryResponse {
  id: number;
  orderNumber: number;
  totalPrice: number;
  status: string;
  payment_status: 'PENDING' | 'FAILURE' | 'SUCCESS' | 'REFUNDED' | 'CANCELLED' | 'EXPIRED';
  transactionId: number;
  currency: string;
  user: {
    phone: number;
  };
  tickets: OrderTicket[];
  item: MatchResponse;
}

// PROFILE

export interface ProfileDetailsResponse {
  phone: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

// HISTORY

export type HistoryResponse = HistoryElement[];

export interface StadiumInfo {
  id: number;
  arName: string;
  kuName: string;
  createdAt: string;
  updatedAt: string;
}

export interface PurchasedTicket {
  id: string;
  holderName: string | null;
  holderPhone: string | null;
  used: boolean;
  sector: string | null;
  price: number;
  currency: string;
  category?: {
    key?: string;
    label?: string;
    color?: string;
  } | null;
  section: string | null;
  row: string | null;
  seatNumber: string | null;
  slotDescription: string | null;
  parentEventId: number | null;
}

export interface HistoryElement {
  id: number;
  orderNumber: string;
  totalPrice: number;
  currency: string;
  ticketOrderCreatedAt: string | Date;
  event: {
    id: number;
    eventType: EventType | string;
    date: string;
    time: string;

    concert?: {
      id: number;
      title: string;
      subTitle: string | null;
      imageLink: string;
    } | null;

    match?: {
      id: number;
      title: string;
      subTitle: string | null;
      teamA: TeamA;
      teamB: TeamB;
    } | null;

    stadium?: {
      id: number;
      name: string;
    } | null;
  };
  tickets: PurchasedTicket[];
}

export enum EventType {
  SPORT,
  CONCERT,
}
export interface Event {
  id: number;
  eventType: EventType;
  eventKey: string;
  state: MatchState;
  dateAndTime: string;
  arAddress: string | null;
  kuAddress: string | null;
  stadium?: StadiumInfo | null;
}

export interface Concert {
  id: number;
  city: string;
  title: string;
  subtitle: string;
  date: string;
  time: string;
  startingPrice: number;
  currency: string;
  eventId: number;
  eventKey: string;
  state: MatchState;
  imageLink: string;
  address: string;
}

export interface Match {
  title: string;
  subtitle: string;
  date: string;
  time: string;
  stadium: string;
  eventKey: string;
  startingPrice: number;
  currency: string;
  city: string;
  state: string;
  teamA: TeamA;
  teamB: TeamB;
  id: number;
}

export interface TeamA {
  name: string;
  logo: string;
  id: number;
}

export interface TeamB {
  name: string;
  logo: string;
  id: number;
}

export interface apiQiTokenResponse {
  accessToken: string;
  refreshToken: string;
  type: string;
  expiresIn: number;
  isCompleted: boolean;
}

export type SingleOrderResponse = {
  eventId: number | undefined;
  eventInfoId: number;
  holdToken: string;
  tickets: {
    sector: string;
    categoryKey: string;
    price: number;
    gate: string;
    holderName: string;
    holderPhone: string;
  }[];

  paymentMethod: string;
  variantId?: number;
  deliveryMethod?: {
    type: string;
    value: string | undefined;
  };
  promoCode?: string;
  sessionId?: number;
};

export type orderNumber = {
  order_number?: string | undefined;
  order_id?: string | undefined;
};
export type OrderStatus = {
  status: string;
};

export interface CreateHoldTokenResponse {
  holdToken: string;
  expiresAt: string;
  expiresInSeconds: number;
}

// B2B — docs/b2b-fe-be-contract.md (TZ-280)

export interface B2BRegistrationResponse {
  id: number;
  fullName: string;
  companyOrOrganization: string | null;
  phoneNumber: string;
  email: string | null;
  city: string;
  websiteOrSocialMedia: string | null;
  hasOrganizedBefore: boolean;
  previousEvents: string | null;
  eventName: string;
  eventType: B2BEventType;
  publicEventCategory: B2BPublicEventCategory | null;
  description: string;
  eventDate: string | null;
  eventTime: string | null;
  eventVenue: string | null;
  isVenueConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Single class-validator style entry (Nest `ValidationPipe` factory default). */
export type B2BRegistrationValidationMessageItem = {
  property?: string;
  constraints?: Record<string, string>;
  children?: unknown[];
};

/** RFC7807-style or custom validation entries with a path segment. */
export type B2BRegistrationStructuredErrorItem = {
  path?: string;
  field?: string;
  message?: string;
  msg?: string;
};

/** Nest-style JSON body on 4xx for POST /consumers/b2b/register (and similar). */
export interface B2BRegistrationErrorBody {
  statusCode?: number;
  message?: string | string[] | B2BRegistrationValidationMessageItem[];
  error?: string;
  errors?: B2BRegistrationStructuredErrorItem[];
}

// International Events consumer API

/** A PlatinumList event; booking happens on PlatinumList via `iframeUrl`. */
export interface InternationalEventResponse {
  id: number;
  title: string;
  subtitle: string | null;
  description: string | null;
  /** Stored `imageUrl`. */
  imageLink: string | null;
  startingPrice: number;
  /** Always `'USD'`. */
  currency: string;
  /** Localized by `X-Custom-Language`. */
  city: string | null;
  country: string | null;
  /** Only `ON_SALE` is surfaced. */
  state: MatchState;
  /** Pre-formatted; render as-is. */
  date: string | null;
  /** Pre-formatted; render as-is. */
  time: string | null;
  /** PlatinumList checkout URL; load in an iframe. */
  iframeUrl: string;
  /** Optional notes/notice cards (ملاحظات، تنبيه) shown like the concert page. */
  goodToKnow?: GoodToKnowItem[];
}

/** Where the feature is exposed; set from the dashboard. */
export type InternationalEventsMode = 'ALL' | 'WEB' | 'SUPER_QI' | 'DISABLED';

export interface InternationalEventsResponse {
  items: InternationalEventResponse[];
  /** Dashboard-controlled exposure mode. */
  mode: InternationalEventsMode;
  /** When true, render events as a compact list instead of the card grid. */
  isCompactList?: boolean;
}
